/** Плашка покрытия себестоимости рядом с плашкой сверки: «Нет себестоимости: N товаров, X ₽
 *  выручки» (+ не сопоставленные с 1С отдельно), «цены на …», ссылка на раздел «Себестоимость» →
 *  «Без цены» (если есть право `purchase_prices`). Только у площадки с себестоимостью (`mp.cogs`).
 *  Ошибка ручки — красной плашкой с текстом, не молчанием. */
import React from "react";
import { Link } from "react-router-dom";

import type { CogsCoverage } from "@/api/mpAnalytics";
import type { OzFilters } from "@/api/ozonAnalytics";
import { useAuth } from "@/auth/useAuth";
import { cn } from "@/lib/utils";
import { coveragePlaque, missingPricesUrl, PURCHASE_PRICES_URL } from "./cogs";
import { useMp } from "./mp";
import { useCogsCoverage } from "./queries";
import { apiErrorText } from "./ui";

const TONE = {
  warn: "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
  none: "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
  ok: "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  before: "text-muted-foreground",
} as const;

/** Право на раздел «Себестоимость» (`purchase_prices`; там вкладка «Без цены»). */
function usePurchasePricesAccess(): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return user.is_admin || user.permissions.includes("purchase_prices");
}

export function CogsCoverageChip({
  filters,
  enabled,
}: {
  filters: OzFilters;
  /** false — вкладка «План»: фильтры периода там не действуют, запрос не нужен */
  enabled: boolean;
}): React.ReactElement | null {
  const mp = useMp();
  // у площадки без себестоимости нет ни ручки, ни плашки (сейчас есть у обеих)
  return mp.cogs ? <Coverage filters={filters} enabled={enabled} /> : null;
}

function Coverage({
  filters,
  enabled,
}: {
  filters: OzFilters;
  enabled: boolean;
}): React.ReactElement | null {
  const mp = useMp();
  const q = useCogsCoverage(mp.id, filters.date_from, filters.date_to, filters.legal, enabled);
  if (q.isError) {
    return (
      <span
        data-testid="cogs-coverage-error"
        className="rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
      >
        покрытие себестоимости недоступно: {apiErrorText(q.error, mp.label)}
      </span>
    );
  }
  if (!enabled || !q.data) return null;
  return (
    <CogsCoverageView
      coverage={q.data}
      mpId={mp.id}
      dateFrom={filters.date_from}
      dateTo={filters.date_to}
      legal={filters.legal}
    />
  );
}

/** Плашка по готовому покрытию (`/cogs-coverage` раздела или блок площадки во вкладке «План»):
 *  ссылка «Без цены» — за `dateFrom`…`dateTo` и юрлицо `legal` (null — все). */
export function CogsCoverageView({
  coverage,
  mpId,
  dateFrom,
  dateTo,
  legal,
}: {
  coverage: Pick<CogsCoverage, "cogs" | "covered_from" | "date_from" | "statuses">;
  mpId: string;
  dateFrom: string;
  dateTo: string;
  legal: string | null;
}): React.ReactElement {
  const canPrices = usePurchasePricesAccess();
  const pl = coveragePlaque(coverage);
  return (
    <span
      data-testid="cogs-coverage"
      data-kind={pl.kind}
      className={cn("rounded-full border px-2 py-0.5", TONE[pl.kind])}
    >
      {pl.parts.join("; ")}
      {pl.prices && <span className="opacity-75"> · {pl.prices}</span>}
      {pl.kind === "warn" && canPrices && (
        <>
          {" · "}
          <Link
            to={missingPricesUrl(dateFrom, dateTo, legal, mpId)}
            className="font-medium underline"
            data-testid="cogs-missing-link"
          >
            Себестоимость → «Без цены»
          </Link>
        </>
      )}
      {pl.kind === "none" && canPrices && (
        <>
          {" · "}
          <Link to={PURCHASE_PRICES_URL} className="font-medium underline" data-testid="cogs-prices-link">
            Раздел «Себестоимость»
          </Link>
        </>
      )}
    </span>
  );
}
